import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { useOutletContext } from "react-router-dom"
import StateData from 'components/reporting/components/StateData/StateData'
import Bloc from 'components/reporting/components/Bloc/Bloc'
import DoughnutChart from 'components/reporting/components/graphs/Doughnut/Doughnut'
import HistogramChart from 'components/reporting/components/graphs/Histogram/Histogram'
import { formatNb } from 'functions'
import { getDatasFrom } from 'helpers/chartjs'
import colors from 'components/reporting/styles/colors.scss'

const OverviewUnified = () => {
  const { t } = useTranslation()
  const [contract] = useOutletContext()
  const [currentContract, setCurrentContract] = useState(null)
  const [insuredProducts, setInsuredProducts] = useState(null)
  const [claimsOpened, setClaimsOpened] = useState(null)
  const [totalRevenue, setTotalRevenue] = useState(null)
  const [revenuePerProduct, setRevenuePerProduct] = useState(null)
  const [insurancesAndClaims, setInsurancesAndClaims] = useState(null)
  const [adhesionPerPackage, setAdhesionPerPackage] = useState(null)
  const [topDistributors, setTopDistributors] = useState(null)
  const [topClaims, setTopClaims] = useState(null)
  const totalAdhesionPerPackage = formatNb(adhesionPerPackage?.data?.reduce( (acc, cur) => acc + cur ,0))
  const totalDistributors = formatNb(topDistributors?.data?.reduce( (acc, cur) => acc + cur ,0))
  

  useEffect(() => {

    const getDatas = async () => {
      if (currentContract === contract._id) return
      setCurrentContract(contract._id)

      // Reset datas
      setInsuredProducts(null)
      setClaimsOpened(null)
      setTotalRevenue(null)
      setRevenuePerProduct(null)
      setInsurancesAndClaims(null)
      setAdhesionPerPackage(null)
      setTopDistributors(null)
      setTopClaims(null)
      setRevenuePerProduct(null)
      setInsurancesAndClaims(null)

      try {
        Promise.all([
          getDatasFrom({ contract, endpoint: 'insuredProductsOptimized' }),
          getDatasFrom({ contract, endpoint: 'claimsOpenedOptimized' }),
          getDatasFrom({ contract, endpoint: 'totalRevenueOptimized' }),
          getDatasFrom({ contract, endpoint: 'revenuePerProductOptimized' }),
          getDatasFrom({ contract, endpoint: 'insurancesAndClaimsOptimized' }),
          getDatasFrom({ contract, endpoint: 'adhesionPerPackageOptimized' }),
          getDatasFrom({ contract, endpoint: 'topDistributorsOptimized' }),
          getDatasFrom({ contract, endpoint: 'topClaimsOptimized' }),
        ]).then( values => {
          if (values[0]) setInsuredProducts(values[0])
          if (values[1]) setClaimsOpened(values[1])
          if (values[2]) setTotalRevenue(values[2])
          if (values[3]) setRevenuePerProduct(values[3])
          if (values[4]) setInsurancesAndClaims(values[4])
          if (values[5]) setAdhesionPerPackage(values[5])
          if (values[6]) setTopDistributors(values[6])
          if (values[7]) setTopClaims(values[7])
        })
      } catch (error) {
        console.log('Fetch datas error')
      }
    }
    if (contract?._id) getDatas()
  }, [contract, currentContract])

  return (
    <>
      <StateData
        title={t('reporting:graphs.insuredProducts')}
        data={insuredProducts}
        size={!contract?.partner ? 'mid' : undefined}
        footerTitle={t('reporting:graphs.pastMonth')} 
      />

      {contract?.partner && <StateData
        title={t('reporting:graphs.totalRevenue')}
        data={totalRevenue}
        footerTitle={t('reporting:graphs.pastMonth')}
      />}

      <StateData
        title={t('reporting:graphs.claimsOpened')}
        data={claimsOpened}
        size={!contract?.partner ? 'mid' : undefined}
        footerTitle={t('reporting:graphs.thisMonth')}
      />

      {contract?.partner && <Bloc
        title={t('reporting:graphs.revenuePerProduct.title')}
        subtitle={t('reporting:graphs.range', {type: '(Euros)'})}
        size='mid'
        content={<HistogramChart
          backgroundColor={[
            colors.blue1,
            colors.blue4,
            colors.orange2,
            colors.orange4
          ]}
          legendID='legendRevenuePerProduct'
          datas={revenuePerProduct}
          stacked={true}
          monthsAxisX={true}
          dataType={'€'}
        />}
      />}

      <Bloc
        title={t('reporting:graphs.insurancesAndClaims.title')}
        subtitle={t('reporting:graphs.range', {type: null})}
        size={contract?.partner && 'mid'}
        content={<HistogramChart
          backgroundColor={[
            colors.blue4,
            colors.blue1
          ]}
          legendID='legendInsurancesAndClaims'
          datas={insurancesAndClaims}
          monthsAxisX={true}
        />}
      />

      <Bloc
        title={t('reporting:graphs.adhesionPerPackage.title')}
        subtitle={t('reporting:graphs.adhesionPerPackage.subtitle', {number: totalAdhesionPerPackage})}
        size='tier'
        content={<DoughnutChart
          backgroundColor={[
            colors.blue1,
            colors.blue2,
            colors.blue3,
            colors.blue4,
            colors.blue5
          ]}
          legendID='legendRateType'
          datas={adhesionPerPackage}
        />}
      />

      <Bloc
        title={t('reporting:graphs.topDistributor.title')}
        subtitle={t('reporting:graphs.topDistributor.subtitle', {number: totalDistributors})}
        size='tier'
        content={<DoughnutChart
          backgroundColor={[
            colors.orange1,
            colors.orange2,
            colors.orange3,
            colors.orange4,
            colors.orange5
          ]}
          legendID='legendTopDistributor'
          datas={topDistributors}
        />}
      />

      <Bloc
        title={t('reporting:graphs.topClaims.title')}
        subtitle={t('reporting:graphs.topClaims.subtitle')}
        size='tier'
        content={<DoughnutChart
          backgroundColor={[
            colors.grey1,
            colors.grey2,
            colors.grey3,
            colors.grey4,
            colors.grey5
          ]}
          legendID='legendTopClaims'
          datas={topClaims}
        />}
      />
    </>
  )
};

export default OverviewUnified
